import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormLabel,
    ListItemText,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Switch
} from "@mui/material";
import type {
    CreateIntegrationRequest,
    EmbedAssertedEmailSource,
    EmbedEmailCapturePolicy
} from "../../../../common/interfaces/admin/integration.interface";
import AllowedOriginsEditor from "./allowed-origins-editor.comp";

const ASSERTED_SOURCE_OPTIONS: { value: EmbedAssertedEmailSource; label: string }[] = [
    { value: "asserted_explicit_config", label: "Explicit config (window.MBAssist.identify)" },
    { value: "asserted_data_attribute", label: "Script tag data-* attribute" },
    { value: "asserted_meta_tag", label: "<meta> tag" },
    { value: "asserted_configured_global", label: "Custom JS global variable" },
    { value: "asserted_configured_cookie", label: "Cookie" }
];

/**
 * Retention presets. `null` = retain indefinitely (the default, and what every
 * pre-v2.0.9 integration carries). The remaining values map to whole-number
 * days; anything other than these is still server-accepted (range 7–3650),
 * but the UI only exposes the curated set to keep the form simple.
 */
const RETENTION_OPTIONS: { value: number | null; label: string }[] = [
    { value: null, label: "Retain indefinitely (default)" },
    { value: 30, label: "30 days" },
    { value: 60, label: "60 days" },
    { value: 90, label: "90 days" },
    { value: 180, label: "180 days" },
    { value: 365, label: "365 days (1 year)" },
    { value: 730, label: "730 days (2 years)" }
];

export interface IntegrationBehaviorStepProps {
    value: Pick<
        CreateIntegrationRequest,
        | "allowedOrigins"
        | "emailCapturePolicy"
        | "acceptAssertedEmail"
        | "assertedEmailSources"
        | "widgetEnabled"
        | "retentionDays"
    >;
    onChange(patch: Partial<IntegrationBehaviorStepProps["value"]>): void;
}

export default function IntegrationBehaviorStep({ value, onChange }: IntegrationBehaviorStepProps) {
    return (
        <div className="integration-step">
            <AllowedOriginsEditor
                value={value.allowedOrigins ?? []}
                onChange={next => onChange({ allowedOrigins: next })}
                disabled={!value.widgetEnabled}
            />

            <FormControl>
                <FormLabel>Email capture policy</FormLabel>
                <RadioGroup
                    row
                    value={value.emailCapturePolicy ?? "never"}
                    onChange={e =>
                        onChange({
                            emailCapturePolicy: e.target.value as EmbedEmailCapturePolicy
                        })
                    }
                >
                    <FormControlLabel value="never" control={<Radio />} label="Never" />
                    <FormControlLabel value="optional" control={<Radio />} label="Optional" />
                    <FormControlLabel value="required" control={<Radio />} label="Required" />
                </RadioGroup>
            </FormControl>

            <FormControlLabel
                control={
                    <Switch
                        checked={value.acceptAssertedEmail ?? false}
                        onChange={(_, v) => onChange({ acceptAssertedEmail: v })}
                    />
                }
                label="Accept asserted email from host page"
            />

            <FormControl disabled={!value.acceptAssertedEmail}>
                <FormLabel>Asserted email sources</FormLabel>
                <Select
                    multiple
                    value={value.assertedEmailSources ?? []}
                    onChange={e =>
                        onChange({
                            assertedEmailSources: (typeof e.target.value === "string"
                                ? e.target.value.split(",")
                                : e.target.value) as EmbedAssertedEmailSource[]
                        })
                    }
                    renderValue={selected =>
                        (selected as EmbedAssertedEmailSource[])
                            .map(v => ASSERTED_SOURCE_OPTIONS.find(o => o.value === v)?.label ?? v)
                            .join(", ")
                    }
                >
                    {ASSERTED_SOURCE_OPTIONS.map(opt => (
                        <MenuItem key={opt.value} value={opt.value}>
                            <Checkbox
                                checked={(value.assertedEmailSources ?? []).indexOf(opt.value) > -1}
                            />
                            <ListItemText primary={opt.label} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl>
                <FormLabel>Message retention</FormLabel>
                <Select
                    // null sentinel in the option list maps to a string "none" in
                    // the MUI Select value (MUI doesn't accept null as a value);
                    // round-trip through "none" on the way in and out.
                    value={value.retentionDays == null ? "none" : String(value.retentionDays)}
                    onChange={e => {
                        const raw = e.target.value;
                        const next = raw === "none" ? null : Number(raw);
                        onChange({ retentionDays: next });
                    }}
                >
                    {RETENTION_OPTIONS.map(opt => (
                        <MenuItem
                            key={opt.value == null ? "none" : opt.value}
                            value={opt.value == null ? "none" : String(opt.value)}
                        >
                            {opt.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </div>
    );
}
